import { useTranslation } from "react-i18next";
import { Comment, Button, Notification } from "@arco-design/web-react";
import { IconDelete } from "@arco-design/web-react/icon";
import { useMutation } from "@tanstack/react-query";

import ContentPreview from "../Post/ContentPreview";


import { CommentApi, PostApi } from "../../types/types";
import { updatePostRequest } from "../../../../../../api/feed";
import { UserAvatar } from "../../../../../../components/UserAvatar/UserAvatar";
import { formatDate } from "../../../../../../utils/formatDate";
import { deleteCommentFormData } from "../../../../../../utils/deleteCommentFormData";

import './Comment.css';
import UpdateCommentModal from "../UpdateCommentModal";

interface ICommentProps {
  comment: CommentApi;
  post: PostApi;
  currentUserId: number;
  isMutating: boolean;
  isCurrentUserAdmin: boolean;
  setIsMutating: (value: boolean) => void;
  refetch: () => void;
  onUpdateComment: (postId: number, updatedPost: FormData) => void;
}

const CommentComponent = ({
  comment,
  post,
  currentUserId,
  isCurrentUserAdmin,
  isMutating,
  setIsMutating,
  refetch,
  onUpdateComment,
}: ICommentProps) => {
  const { t } = useTranslation();

  const editedDate = comment.created_at !== comment.edited_at &&
    ` | ${t('home_page.edited')} - ${formatDate(comment.edited_at)}`;

  const { mutateAsync: deleteComment } = useMutation({
    mutationFn: updatePostRequest,
    onSuccess: () => {
      Notification.success({
        content: t('notification_messages.comment_deleted_success'),
      });
      setIsMutating(false);
      refetch();
    },
    onError: () => {
      Notification.error({
        content: t('notification_messages.comment_deleted_error'),
      });
      setIsMutating(false);
    },
  });

  const handleDeleteComment = (post: PostApi, comment: CommentApi) => {
    setIsMutating(true);

    const formData = deleteCommentFormData({post, comment});

    deleteComment([post.id, formData]);
  };

  const renderCommentActionsButtons = () => {
    return [
      <UpdateCommentModal
        comment={comment}
        post={post}
        isMutating={isMutating}
        onUpdatePost={onUpdateComment}
      />,
      <Button
        className="comment__actions-button"
        disabled={isMutating}
      >
        <IconDelete
          onClick={() => handleDeleteComment(post, comment)}
        />
      </Button>
    ]
  }

  return (
    <Comment
      actions={
        comment.user_id === currentUserId || isCurrentUserAdmin
          ? renderCommentActionsButtons()
          : undefined
      }
      content={
        <div className="comment__content">
          <div className="comment__content-avatar">
            <UserAvatar
              avatarUrl={comment.avatar_url}
              fullName={comment.user_name}
              size={24}
              shape="circle"
              alt={comment.user_name}
            />
          </div>
          <div className="comment__content-info">
            <b>{comment.user_name}</b>
            <span className="comment__content-info_date">
              {formatDate(comment.created_at)}
              {editedDate}
            </span>
            <ContentPreview content={comment.content} />
          </div>
        </div>
      }
      className="sub-comment"
    />
  );
};

export default CommentComponent;
